import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, Check, Search } from 'lucide-react'

/**
 * Custom styled Select component that matches the admin portal's theme.
 * Fully supports dark mode and functions as a drop-in replacement for native selects.
 * 
 * @param {Object} props
 * @param {string|number} props.value - Currently selected value
 * @param {Function} props.onChange - Callback receiving the new value, e.g. onChange={(val) => setMyState(val)}
 * @param {Array<{value: string|number, label: string}>} props.options - Array of option objects
 * @param {string} props.placeholder - Text to display when no value is selected
 * @param {boolean} props.disabled - Disables the select input
 * @param {string} props.className - Additional CSS classes for the container
 * @param {string} props.id - ID for the button element
 * @param {string} props.name - Name attribute
 * @param {boolean} props.searchable - Enables a search input to filter options
 */
export default function Select({
  value,
  onChange,
  options = [],
  placeholder = 'Select an option...',
  disabled = false,
  className = '',
  id,
  name,
  searchable = false,
  menuPlacement = 'bottom' // 'top' or 'bottom'
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef(null)
  const closeTimeoutRef = useRef(null)

  const handleClose = () => {
    if (isClosing) return
    setIsClosing(true)
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
      setIsClosing(false)
      setSearchQuery('')
    }, 140)
  }

  const handleOpen = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
    setIsClosing(false)
    setIsOpen(true)
  }

  // Close when clicking outside of the component
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (isOpen && !isClosing) {
          handleClose()
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, isClosing])

  const selectedOption = options.find(opt => String(opt.value) === String(value))

  const filteredOptions = searchable && searchQuery.trim()
    ? options.filter(opt => {
      if (opt.options || opt.isGroup) return true
      return opt.label?.toLowerCase().includes(searchQuery.toLowerCase())
    })
    : options

  return (
    <div ref={containerRef} className={`relative w-full text-left ${className}`}>
      <button
        type='button'
        id={id}
        name={name}
        disabled={disabled}
        onClick={() => {
          if (disabled || isClosing) return
          if (isOpen) handleClose()
          else handleOpen()
        }}
        className={`w-full flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg shadow-sm text-sm outline-none transition-all duration-200 
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400' : 'cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500/20'}`}
      >
        <span className={`block truncate ${!selectedOption ? 'text-gray-400 dark:text-gray-500 dark:text-slate-500' : 'text-gray-900 dark:text-white'}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 dark:text-gray-400 dark:text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {(isOpen || isClosing) && (
        <div
          className={`absolute z-[100] w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden duration-150 ${isClosing ? 'animate-out fade-out zoom-out-95 pointer-events-none' : 'animate-in fade-in zoom-in-95'
            } ${menuPlacement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
        >
          <ul className='max-h-60 overflow-auto py-1 text-sm outline-none no-scrollbar' role='listbox'>
            {searchable && (
              <li className='px-2 py-1.5 sticky top-0 bg-white dark:bg-slate-800 z-10'>
                <div className='relative'>
                  <Search size={14} className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500' />
                  <input
                    type='text'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder='Search...'
                    autoFocus
                    className='w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-slate-600 rounded-md bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:border-blue-400 dark:focus:border-blue-500 transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500'
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </li>
            )}
            {filteredOptions.length === 0 ? (
              <li className='px-3 py-2 text-gray-500 dark:text-gray-400 dark:text-slate-400 text-center select-none'>
                {searchQuery ? 'No matches found' : 'No options available'}
              </li>
            ) : (
              filteredOptions.map((option, index) => {
                // If this is an optgroup (has 'options' array or 'isGroup' flag)
                if (option.options || option.isGroup) {
                  return (
                    <div key={`group-${index}`}>
                      <li className='px-3 py-1.5 text-xs font-bold text-gray-400 dark:text-gray-500 dark:text-slate-500 uppercase tracking-wider select-none bg-gray-50/50 dark:bg-slate-800/80'>
                        {option.label}
                      </li>
                      {(option.options || []).map((subOption, subIndex) => {
                        const isSelected = String(subOption.value) === String(value)
                        return (
                          <li
                            key={`sub-${subOption.value}-${subIndex}`}
                            role='option'
                            aria-selected={isSelected}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              onChange(subOption.value)
                              handleClose()
                            }}
                            className={`relative cursor-pointer select-none py-2.5 pl-5 pr-9 transition-colors
                              ${isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-semibold'
                                : 'text-gray-700 dark:text-gray-300 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-900 dark:hover:bg-slate-700/50 hover:text-gray-900 dark:hover:text-white'
                              }`}
                          >
                            <span className='block truncate'>{subOption.label}</span>
                            {isSelected && (
                              <span className='absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600 dark:text-blue-400'>
                                <Check className='w-4 h-4' />
                              </span>
                            )}
                          </li>
                        )
                      })}
                    </div>
                  )
                }

                // Normal option
                const isSelected = String(option.value) === String(value)
                return (
                  <li
                    key={`${option.value}-${index}`}
                    role='option'
                    aria-selected={isSelected}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (option.disabled) return
                      onChange(option.value)
                      handleClose()
                    }}
                    className={`relative select-none py-2.5 pl-3 pr-9 transition-colors
                      ${option.disabled
                        ? 'text-gray-400 dark:text-gray-500 dark:text-slate-500 cursor-not-allowed bg-gray-50 dark:bg-slate-900 dark:bg-slate-800/50'
                        : isSelected
                          ? 'cursor-pointer bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-semibold'
                          : 'cursor-pointer text-gray-700 dark:text-gray-300 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-900 dark:hover:bg-slate-700/50 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    <span className='block truncate'>{option.label}</span>

                    {isSelected && !option.disabled && (
                      <span className='absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600 dark:text-blue-400'>
                        <Check className='w-4 h-4' />
                      </span>
                    )}
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
